import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    projectDetails: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Handle scroll for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4qk6epmma3c958r86j0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          service: '',
          projectDetails: ''
        });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="px-8 lg:px-24 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-black text-2xl transition-colors ${isScrolled ? 'bg-[#FFB81C] text-[#1A2B4A]' : 'bg-white text-[#1A2B4A]'}`}>
                DE
              </div>
              <span className={`font-bold text-lg tracking-wide ${isScrolled ? 'text-[#1A2B4A]' : 'text-white'}`}>
                DUNNAWAY EXCAVATING
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#services" className={`font-medium transition-colors hover:text-[#FFB81C] ${isScrolled ? 'text-[#1A2B4A]' : 'text-white'}`}>Services</a>
              <a href="#gallery" className={`font-medium transition-colors hover:text-[#FFB81C] ${isScrolled ? 'text-[#1A2B4A]' : 'text-white'}`}>Gallery</a>
              <a href="#about" className={`font-medium transition-colors hover:text-[#FFB81C] ${isScrolled ? 'text-[#1A2B4A]' : 'text-white'}`}>About</a>
              <a href="#contact" className={`font-medium transition-colors hover:text-[#FFB81C] ${isScrolled ? 'text-[#1A2B4A]' : 'text-white'}`}>Contact</a>
              <a 
                href="#contact" 
                className="bg-[#FFB81C] text-[#1A2B4A] px-6 py-3 rounded-full font-bold whitespace-nowrap hover:bg-[#e5a519] transition-colors cursor-pointer shadow-xl"
              >
                Get Estimate
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-[#1A2B4A]' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-6 pb-6 flex flex-col gap-4 bg-white/95 backdrop-blur-sm rounded-2xl p-6">
              <a href="#services" className="text-[#1A2B4A] font-medium hover:text-[#FFB81C]" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#gallery" className="text-[#1A2B4A] font-medium hover:text-[#FFB81C]" onClick={() => setMobileMenuOpen(false)}>Gallery</a>
              <a href="#about" className="text-[#1A2B4A] font-medium hover:text-[#FFB81C]" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#contact" className="text-[#1A2B4A] font-medium hover:text-[#FFB81C]" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <a href="#contact" className="bg-[#FFB81C] text-[#1A2B4A] px-6 py-3 rounded-full font-bold text-center whitespace-nowrap hover:bg-[#e5a519] transition-colors cursor-pointer shadow-xl">
                Get Estimate
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/40ffc658b1b9500387d803035aa9abaa.jpeg"
            alt="Excavating Equipment at Work"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 px-8 lg:px-24 w-full">
          <div className="max-w-2xl">
            <h1 className="text-6xl lg:text-7xl xl:text-8xl font-extrabold text-white leading-tight mb-6" style={{ textShadow: '0 4px 12px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.6)' }}>
              Professional Excavation Company Serving the Lake of the Ozarks Since 1966
            </h1>
            <p className="text-xl lg:text-2xl text-white mb-12 leading-relaxed max-w-xl font-medium" style={{ textShadow: '0 3px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.6)' }}>
              Family-owned excavating and demolition services for homes and small businesses in Gravois Mills, Morgan County, and throughout the Lake of the Ozarks area.
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <a 
                href="#contact" 
                className="bg-[#FFB81C] text-[#1A2B4A] px-10 py-5 rounded-full font-bold text-lg flex items-center justify-center gap-3 hover:gap-3 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-xl"
              >
                Request Free Estimate
                <span className="w-8 h-8 bg-[#1A2B4A] rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white text-sm"></i>
                </span>
              </a>
              <a 
                href="tel:5733745867" 
                className="border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#1A2B4A] transition-all whitespace-nowrap cursor-pointer text-center shadow-xl"
              >
                Call (573) 374-5867
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-[#F8F6F3] py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          <div className="mb-20">
            <div className="inline-block bg-[#FFB81C] text-[#1A2B4A] px-8 py-3 rounded-full text-sm font-bold tracking-wider uppercase mb-6">
              OUR SERVICES
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-[#1A2B4A] mb-4">
              Expert Excavation &amp; Demolition Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="w-20 h-20 bg-[#FFF8E7] rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-plant-line text-4xl text-[#1A2B4A]"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1A2B4A] mb-5">
                Land Clearing &amp; Site Preparation
              </h3>
              <p className="text-lg text-[#5A6B7D] leading-relaxed mb-6">
                Complete land clearing and site preparation including brush removal, tree and stump removal, demolition, and grading for new construction or property improvements. Professional excavation services for residential and small commercial projects throughout Lake of the Ozarks.
              </p>
              <a href="#contact" className="text-[#FFB81C] font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="w-20 h-20 bg-[#FFF8E7] rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-home-4-line text-4xl text-[#1A2B4A]"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1A2B4A] mb-5">
                Basement &amp; Foundation Excavation
              </h3>
              <p className="text-lg text-[#5A6B7D] leading-relaxed mb-6">
                Professional basement and foundation excavation with attention to depth, grading, compaction, and drainage management. Expert excavating contractor services ideal for new home construction, additions, or rebuilding projects in the Lake and surrounding areas.
              </p>
              <a href="#contact" className="text-[#FFB81C] font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="w-20 h-20 bg-[#FFF8E7] rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-water-flash-line text-4xl text-[#1A2B4A]"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1A2B4A] mb-5">
                Utility Trenching &amp; Installation
              </h3>
              <p className="text-lg text-[#5A6B7D] leading-relaxed mb-6">
                Precision trenching for water lines, sewer lines, and electrical conduit. Clean trench cuts, proper bedding, and safe backfilling to protect your utilities for decades. Trusted excavation company for utility installation needs.
              </p>
              <a href="#contact" className="text-[#FFB81C] font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Service Card 4 */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="w-20 h-20 bg-[#FFF8E7] rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-drop-line text-4xl text-[#1A2B4A]"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1A2B4A] mb-5">
                Drainage Solutions &amp; Erosion Control
              </h3>
              <p className="text-lg text-[#5A6B7D] leading-relaxed mb-6">
                Custom drainage systems designed to reduce erosion and manage water movement. Includes curtain drains, French drains, swales, downspout drainage, and shoreline water-control solutions tailored to Missouri terrain and Lake of the Ozarks properties.
              </p>
              <a href="#contact" className="text-[#FFB81C] font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Service Card 5 */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="w-20 h-20 bg-[#FFF8E7] rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-road-map-line text-4xl text-[#1A2B4A]"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1A2B4A] mb-5">
                Private Road &amp; Driveway Construction
              </h3>
              <p className="text-lg text-[#5A6B7D] leading-relaxed mb-6">
                Construction and grading of gravel driveways and long private roads. Includes road base installation, crown grading, drainage control, and road and driveway construction suited to Lake of the Ozarks terrain. Expert excavating for all access road needs.
              </p>
              <a href="#contact" className="text-[#FFB81C] font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Service Card 6 */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="w-20 h-20 bg-[#FFF8E7] rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-contrast-drop-2-line text-4xl text-[#1A2B4A]"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1A2B4A] mb-5">
                Pond Building &amp; Rock Removal
              </h3>
              <p className="text-lg text-[#5A6B7D] leading-relaxed mb-6">
                Full-service excavation for ponds, lake-edge shaping, and water-feature development. Includes rock breaking and removal, careful depth planning, and drainage considerations.
              </p>
              <a href="#contact" className="text-[#FFB81C] font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="about" className="bg-gradient-to-b from-[#E8F4F8] to-white py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            {/* Left - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border-8 border-white shadow-xl">
                <img 
                  src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/0b56cacec0d56e93d01120ad61f6a41c.jpeg"
                  alt="Dunnaway Excavating Heritage Since 1966"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 left-6 bg-[#FFB81C] text-white px-6 py-3 rounded-full font-bold text-lg">
                  EST. 1966
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <div className="text-[#FFB81C] text-sm font-bold tracking-widest uppercase mb-4">
                WHY CHOOSE US
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-[#1A2B4A] mb-12 leading-tight">
                Two Generations of Excavation Excellence
              </h2>

              <div className="space-y-9">
                {/* Feature 1 */}
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-[#FFB81C] rounded-full flex items-center justify-center">
                    <i className="ri-home-heart-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">
                      Family-Owned Excavation Company Since 1966
                    </h3>
                    <p className="text-[#5A6B7D] text-lg leading-relaxed">
                      Two generations of trusted excavating and demolition service in the Lake of the Ozarks community. We treat every project like it's our own property. Our excavation and land clearing services are commonly performed together to prepare your site for construction.
                    </p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-[#FFB81C] rounded-full flex items-center justify-center">
                    <i className="ri-tools-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">
                      Professional Equipment &amp; Expertise
                    </h3>
                    <p className="text-[#5A6B7D] text-lg leading-relaxed">
                      Modern excavation equipment and decades of hands-on experience ensure every job is done safely and efficiently.
                    </p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-[#FFB81C] rounded-full flex items-center justify-center">
                    <i className="ri-chat-check-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">
                      Honest Estimates &amp; Clear Communication
                    </h3>
                    <p className="text-[#5A6B7D] text-lg leading-relaxed">
                      No surprises, no hidden fees. We provide transparent pricing and keep you informed throughout every step of your excavation project.
                    </p>
                  </div>
                </div>

                {/* Feature 4 */}
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-[#FFB81C] rounded-full flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">
                      Jobs Done Right &amp; Cleaned Up
                    </h3>
                    <p className="text-[#5A6B7D] text-lg leading-relaxed">
                      We take pride in quality workmanship and leave your property clean and ready. Your satisfaction is our guarantee.
                    </p>
                  </div>
                </div>
              </div>

              <a 
                href="#contact" 
                className="inline-block mt-12 bg-[#1A2B4A] text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-[#2a3b5a] transition-all whitespace-nowrap cursor-pointer"
              >
                Get Your Estimate <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Lake of the Ozarks Excavation Challenges Section */}
      <section className="bg-white py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="text-[#FFB81C] text-sm font-bold tracking-widest uppercase mb-4">
                UNDERSTANDING LAKE TERRAIN
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#1A2B4A] mb-6">
                Excavating at the Lake of the Ozarks
              </h2>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-6">
                Why Lake Terrain Changes How Excavation Is Done
              </h3>
            </div>

            <div className="bg-[#F8F6F3] rounded-3xl p-10 lg:p-14">
              <p className="text-xl text-[#5A6B7D] leading-relaxed mb-16">
                Decades of working on Lake of the Ozarks properties have shown that this terrain behaves differently than flatter ground. Steep slopes, variable soils, exposed rock, and limited access all influence how excavation, land clearing, drainage, and driveway construction must be planned and executed.
              </p>
              <p className="text-xl text-[#5A6B7D] leading-relaxed mb-16">
                These conditions shape how water moves across a site, how equipment performs, and how work must be sequenced to avoid problems during and after excavation. Many of these conditions are uncommon outside of lake-area terrain and require approaches that are specific to the Lake of the Ozarks.
              </p>

              <div className="space-y-12">
                {/* Drainage */}
                <div className="border-l-4 border-[#FFB81C] pl-8">
                  <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">
                    Drainage on Steep Terrain: You Don't Stop the Water — You Steer It
                  </h3>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Water at the lake doesn't behave like it does on flat land. Gravity pulls runoff downhill toward the Lake, and you can't stop it — you can only steer it. The goal is to control where water goes and how fast it moves, not to block it.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Thin soil, exposed rock, and steep grades cause runoff to move quickly and concentrate into defined paths. Once excavation or land clearing begins, those paths can change rapidly. If drainage is not addressed early, erosion and long-term maintenance issues can develop in a short period of time.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-8">
                    Effective lake-area excavation focuses on anticipating how water will move across the site and shaping grades to guide runoff safely, rather than reacting after problems appear.
                  </p>
                  
                  <h4 className="text-xl font-bold text-[#1A2B4A] mb-3">
                    Elevation Decisions and Drainage Behavior
                  </h4>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    At the Lake of the Ozarks, drainage outcomes are largely determined by house elevation and placement. On steep lots, lower placements can direct uphill runoff toward the structure and create steep or impractical driveway approaches that are difficult to correct later.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed">
                    While lowering a home may reduce wall height on the lake side, it often shifts that wall height requirement to the uphill side, where taller back walls or retaining walls could become costly or impractical. Because water always moves downhill toward the lake, these elevation decisions shape drainage behavior, access, and long-term site usability and benefit from early evaluation by an excavation contractor familiar with lake-area terrain.
                  </p>
                </div>

                <div className="h-px bg-[#E0DDD8]"></div>

                {/* Variable Ground Conditions */}
                <div className="border-l-4 border-[#FFB81C] pl-8">
                  <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">
                    Variable Ground Conditions Across Lake Properties
                  </h3>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Ground conditions around the Lake of the Ozarks vary significantly depending on location and elevation. Ridge tops and higher ground often have only a thin layer of soil before excavation encounters a thick, cemented layer commonly referred to locally as hardpan.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    On many lake lots closer to the water, excavation more often encounters combinations of clay and limestone, and in some areas clay and sandstone. These materials behave differently than hardpan but still present their own challenges, particularly on steep slopes.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed">
                    Understanding where these conditions are likely to occur is an important part of planning excavation work at the lake, especially in terrain that places greater demands on both equipment and operators.
                  </p>
                </div>

                <div className="h-px bg-[#E0DDD8]"></div>

                {/* Hardpan and Chert */}
                <div className="border-l-4 border-[#FFB81C] pl-8">
                  <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">
                    Hardpan, Chert, and Abrasive Conditions
                  </h3>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Where hardpan is present, it behaves more like weak concrete than soil when intact. Once broken loose, it breaks down into a dense, gravelly clay that absorbs water, with highly abrasive chert (flint) distributed throughout.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed">
                    These conditions accelerate wear on bucket teeth, blades, cutting edges, tires, and tracks, and they affect how equipment performs during excavation. In abrasive lake-area conditions, equipment wear and behavior are significantly different than on typical soil-based sites, influencing how excavation must be approached and sequenced.
                  </p>
                </div>

                <div className="h-px bg-[#E0DDD8]"></div>

                {/* Limestone and Sandstone */}
                <div className="border-l-4 border-[#FFB81C] pl-8">
                  <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">
                    Limestone and Sandstone Below
                  </h3>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Beneath surface materials, solid bedrock consisting of limestone, or harder dolomite, is common throughout the Lake of the Ozarks area, with sandstone present in some zones. Depending on location, bedrock may be encountered shallow or much deeper below grade.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed">
                    These variations influence excavation depth, equipment choice, and how work progresses from one part of a site to another.
                  </p>
                </div>

                <div className="h-px bg-[#E0DDD8]"></div>

                {/* Steep Slopes */}
                <div className="border-l-4 border-[#FFB81C] pl-8">
                  <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">
                    Working on Steep Slopes and Limited Access
                  </h3>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Steep terrain changes how equipment behaves. Machines that perform well on flat ground can become unstable or inefficient on slopes, requiring slower work, careful positioning, and frequent adjustments.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed">
                    Access is often limited as well. Narrow drives, tight turns, and steep grades can restrict which machines can physically reach a site. Knowing how different equipment handles these conditions comes from experience working in similar terrain.
                  </p>
                </div>

                <div className="h-px bg-[#E0DDD8]"></div>

                {/* Local Experience */}
                <div className="border-l-4 border-[#FFB81C] pl-8">
                  <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">
                    Why Local Experience Matters
                  </h3>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Excavating at the Lake of the Ozarks is about adapting to the terrain rather than trying to overpower it. Water is always moving toward the Lake, soil depth varies, hardpan is present on ridge tops, and rock conditions change from one property to the next.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed mb-4">
                    Decades of working on Lake of the Ozarks properties have shown that techniques that work on flat terrain often don't work here.
                  </p>
                  <p className="text-lg text-[#5A6B7D] leading-relaxed">
                    Familiarity with how water flows across lake-area sites, where hardpan and rock are likely to be encountered, and how slopes affect equipment behavior allows excavation work to be planned realistically and carried out with the terrain in mind.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery Section */}
      <section id="gallery" className="bg-white py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          <div className="text-center mb-20">
            <div className="inline-block bg-[#FFB81C] text-[#1A2B4A] px-8 py-3 rounded-full text-sm font-bold tracking-wider uppercase mb-6">
              PROJECT GALLERY
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-[#1A2B4A] mb-6">
              Our Work Over the Decades
            </h2>
            <p className="text-xl text-[#5A6B7D] max-w-2xl mx-auto">
              Quality excavation projects across the Lake of the Ozarks region
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {/* Gallery Item 1 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/24c776287b09dbf470e5a3f06a644258.png"
                alt="Municipal Waterline Installation Project"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Municipal Waterline Installation</h3>
                  <p className="text-white/90">Laurie, MO • 2002</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 2 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/83cbad2615de843959a6c2f7c83d7780.png"
                alt="Municipal Waterline Installation Project - Excavation Detail"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Municipal Waterline Installation</h3>
                  <p className="text-white/90">Laurie, MO • 2002</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 3 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/ffbc0f79bc0a97887e578d129e6fb6bb.png"
                alt="Road and Driveway Construction Project"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Road & Driveway Construction</h3>
                  <p className="text-white/90">Laurie, MO • 2009</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 4 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/534a61d5e3bd7be083d3009bd49b55dd.jpeg"
                alt="Waterline Installation Project Silver Moon"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Waterline Installation</h3>
                  <p className="text-white/90">Silver Moon • 2012</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 5 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/ae9816bd66c042b6df465d472911b645.png"
                alt="Basement Excavation Project"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Basement Excavation</h3>
                  <p className="text-white/90">Lake Area • 2002</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 6 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/b1dfa14a5f74376840dbd791c1d9f1d1.png"
                alt="Rock Breaking Project"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Rock Breaking</h3>
                  <p className="text-white/90">Lake Area • 2002</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 7 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/c471024d802c16660a7af3ee86a3a80e.png"
                alt="Land Clearing Project"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Land Clearing</h3>
                  <p className="text-white/90">Lake Area • 2024</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 8 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/1dfb51497f3b1adaf7b5d94fdeb03044.png"
                alt="Land Clearing Project with Heavy Equipment"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Land Clearing</h3>
                  <p className="text-white/90">Lake Area • 2024</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 9 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/fc92a67d6487b201339656cb225e16cf.png"
                alt="Cove Work Project"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Cove Work</h3>
                  <p className="text-white/90">Lake Area • 2002</p>
                </div>
              </div>
            </div>

            {/* Gallery Item 10 */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer h-80">
              <img 
                src="https://static.readdy.ai/image/d09808b86f0e4bd9bec455b8a08cd3e5/d4b7d1b49b25832bd04b7266e445c1e8.png"
                alt="Waterline Installation Homewood Subdivision"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Waterline Installation</h3>
                  <p className="text-white/90">Homewood Subdivision • 2001</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process & Pricing Section */}
      <section className="bg-[#FAF8F5] py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          {/* Process */}
          <div className="mb-24">
            <div className="mb-20">
              <div className="inline-block bg-[#FFB81C] text-[#1A2B4A] px-8 py-3 rounded-full text-sm font-bold tracking-wider uppercase mb-6">
                HOW IT WORKS
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-[#1A2B4A]">
                Simple, Straightforward Process
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
              {/* Connecting Line */}
              <div className="hidden lg:block absolute top-11 left-0 right-0 h-1 border-t-4 border-dotted border-[#FFB81C]" style={{ width: 'calc(100% - 88px)', left: '44px' }}></div>

              {/* Step 1 */}
              <div className="text-center relative z-10">
                <div className="w-22 h-22 bg-[#FFB81C] rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto mb-8">
                  1
                </div>
                <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Request Estimate</h3>
                <p className="text-lg text-[#5A6B7D] leading-relaxed">
                  Fill out our simple form or give us a call to describe your project needs.
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center relative z-10">
                <div className="w-22 h-22 bg-[#FFB81C] rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto mb-8">
                  2
                </div>
                <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Site Visit</h3>
                <p className="text-lg text-[#5A6B7D] leading-relaxed">
                  We come to your property to assess the scope and requirements of the job.
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center relative z-10">
                <div className="w-22 h-22 bg-[#FFB81C] rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto mb-8">
                  3
                </div>
                <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Clear Proposal</h3>
                <p className="text-lg text-[#5A6B7D] leading-relaxed">
                  Receive a detailed, transparent estimate with no hidden fees or surprises.
                </p>
              </div>

              {/* Step 4 */}
              <div className="text-center relative z-10">
                <div className="w-22 h-22 bg-[#FFB81C] rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto mb-8">
                  4
                </div>
                <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Job Completion</h3>
                <p className="text-lg text-[#5A6B7D] leading-relaxed">
                  Work completed professionally with your site left clean and ready.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Philosophy Section */}
      <section className="bg-[#1A2B4A] py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block bg-[#FFB81C] text-white px-8 py-3 rounded-full text-sm font-bold tracking-wider uppercase mb-8">
              TRANSPARENT PRICING
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-10">
              Honest Estimates, No Surprises
            </h2>
            <p className="text-xl text-white/85 leading-relaxed max-w-3xl mx-auto mb-16">
              Every project is unique, which is why we provide free on-site estimates. We'll assess your specific needs, explain our process, and give you a clear, detailed quote before any work begins. No hidden fees, no unexpected charges—just honest pricing you can trust.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
              {/* Value 1 */}
              <div>
                <div className="w-16 h-16 bg-[#FFB81C] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-money-dollar-circle-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Free Estimates</h3>
                <p className="text-white/75 text-lg">
                  No charge for site visits and detailed project quotes.
                </p>
              </div>

              {/* Value 2 */}
              <div>
                <div className="w-16 h-16 bg-[#FFB81C] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-message-3-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Clear Communication</h3>
                <p className="text-white/75 text-lg">
                  We explain every detail so you know exactly what to expect.
                </p>
              </div>

              {/* Value 3 */}
              <div>
                <div className="w-16 h-16 bg-[#FFB81C] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-hand-heart-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Fair Pricing</h3>
                <p className="text-white/75 text-lg">
                  Competitive rates with no hidden costs or surprise charges.
                </p>
              </div>
            </div>

            <a 
              href="#contact" 
              className="inline-flex items-center gap-4 bg-[#FFB81C] text-[#1A2B4A] px-12 py-5 rounded-full font-bold text-xl hover:bg-[#e5a519] transition-all whitespace-nowrap cursor-pointer"
            >
              Request Your Free Estimate
              <span className="w-10 h-10 bg-[#1A2B4A] rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line text-white"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="bg-white py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            {/* Left - Content */}
            <div>
              <div className="inline-block bg-[#FFB81C] text-[#1A2B4A] px-8 py-3 rounded-full text-sm font-bold tracking-wider uppercase mb-6">
                SERVICE AREA
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-[#1A2B4A] mb-8 leading-tight">
                Excavation Contractor Serving Lake of the Ozarks Region
              </h2>
              <p className="text-xl text-[#5A6B7D] leading-relaxed mb-12">
                Proudly serving homeowners, small businesses, and builders throughout the Lake of the Ozarks area. We're a local excavation company based in Gravois Mills, Morgan County, Missouri. We know the terrain, and we're committed to serving our community with expert excavating and demolition services. Dunnaway Excavating serves Gravois Mills, Laurie, and surrounding Lake of the Ozarks communities with professional excavation and land clearing.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Laurie</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Sunrise Beach</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Versailles</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Gravois Mills</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Camdenton</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Osage Beach</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Lake Ozark</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FFB81C] rounded-full flex-shrink-0"></div>
                  <span className="text-lg font-semibold text-[#1A2B4A]">Eldon</span>
                </div>
              </div>
            </div>

            {/* Right - Map */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-[500px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d201234.56789!2d-92.6177!3d38.1234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87c3a0a0a0a0a0a0%3A0x0!2sLake%20of%20the%20Ozarks%2C%20MO!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lake of the Ozarks Service Area Map"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="bg-[#F5F5F5] py-24 lg:py-32">
        <div className="px-8 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
            {/* Left - Contact Info */}
            <div>
              <h2 className="text-5xl lg:text-6xl font-bold text-[#1A2B4A] mb-8">
                Get Started with Your Excavation Project Today
              </h2>
              <p className="text-xl text-[#5A6B7D] leading-relaxed mb-12">
                Ready to start your excavating or demolition project? Fill out the form or give us a call. We'll schedule a free site visit and provide you with a detailed estimate for your Lake of the Ozarks excavation needs.
              </p>

              <div className="space-y-8 mb-14">
                {/* Phone */}
                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-[#FFB81C] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-[#1A2B4A] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-[#5A6B7D] uppercase tracking-wider mb-1">PHONE</div>
                    <a href="tel:5733745867" className="text-xl font-bold text-[#1A2B4A] hover:text-[#FFB81C] transition-colors cursor-pointer">
                      (573) 374-5867
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-[#FFB81C] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-[#1A2B4A] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-[#5A6B7D] uppercase tracking-wider mb-1">EMAIL</div>
                    <a href="mailto:dunnaway.excavating@gmail.com" className="text-xl font-bold text-[#1A2B4A] hover:text-[#FFB81C] transition-colors cursor-pointer">
                      dunnaway.excavating@gmail.com
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-[#FFB81C] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-[#1A2B4A] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-[#5A6B7D] uppercase tracking-wider mb-1">ADDRESS</div>
                    <p className="text-lg text-[#1A2B4A]">
                      Gravois Mills, MO 65037<br />
                      Lake of the Ozarks Region
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-[#1A2B4A] mb-6">Business Hours</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-lg">
                    <span className="text-[#5A6B7D]">Monday - Friday</span>
                    <span className="font-semibold text-[#1A2B4A]">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="text-[#5A6B7D]">Weekends</span>
                    <span className="font-semibold text-[#1A2B4A]">By Appointment</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white rounded-3xl p-12 lg:p-14 shadow-lg">
              <form onSubmit={handleSubmit} data-readdy-form id="contact-estimate-form">
                <div className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" className="block text-[#1A2B4A] font-semibold mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleFormChange}
                      required
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFB81C] focus:border-transparent text-lg"
                      placeholder="John Smith"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-[#1A2B4A] font-semibold mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFB81C] focus:border-transparent text-lg"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-[#1A2B4A] font-semibold mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFB81C] focus:border-transparent text-lg"
                      placeholder="(573) 374-5867"
                    />
                  </div>

                  {/* Service Needed */}
                  <div>
                    <label htmlFor="service" className="block text-[#1A2B4A] font-semibold mb-2">
                      Service Needed
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleFormChange}
                      required
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFB81C] focus:border-transparent text-lg cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="Land & Lot Clearing">Land & Lot Clearing</option>
                      <option value="Basements & Foundations">Basements & Foundations</option>
                      <option value="Utility Trenching & Installation">Utility Trenching & Installation</option>
                      <option value="Drainage Solutions">Drainage Solutions</option>
                      <option value="Driveways & Private Roads">Driveways & Private Roads</option>
                      <option value="Pond Building">Pond Building</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Project Details */}
                  <div>
                    <label htmlFor="projectDetails" className="block text-[#1A2B4A] font-semibold mb-2">
                      Tell Us About Your Project
                    </label>
                    <textarea
                      id="projectDetails"
                      name="projectDetails"
                      value={formData.projectDetails}
                      onChange={handleFormChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFB81C] focus:border-transparent text-lg resize-none"
                      placeholder="Please describe your project, location, and any specific requirements..."
                    ></textarea>
                    <div className="text-sm text-[#5A6B7D] mt-2 text-right">
                      {formData.projectDetails.length}/500 characters
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-[#FFB81C] text-[#1A2B4A] py-5 rounded-full font-bold text-xl hover:bg-[#e5a519] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-xl"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send My Request'}
                    <i className="ri-arrow-right-line"></i>
                  </button>

                  {/* Status Messages */}
                  {formStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-xl text-center">
                      Thank you! We'll respond within 24 hours.
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-xl text-center">
                      Something went wrong. Please try again or call us directly.
                    </div>
                  )}

                  <p className="text-center text-[#5A6B7D] text-sm">
                    We'll respond within 24 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1B2E] rounded-t-[32px] pt-20 pb-12">
        <div className="px-8 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-20 max-w-7xl mx-auto">
            {/* Left Column - Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-[#FFB81C] rounded-lg flex items-center justify-center font-black text-lg text-[#1A2B4A]">
                  DE
                </div>
                <h3 className="text-white font-bold text-xl">DUNNAWAY EXCAVATING</h3>
              </div>
              <p className="text-gray-400 text-base mb-5 leading-relaxed">
                Excavating Excellence Since 1966
              </p>
              <p className="text-gray-400 text-base leading-relaxed mb-8">
                Two generations of family-owned excavating service, proudly serving the Lake of the Ozarks community with quality workmanship and honest pricing.
              </p>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/DunnawayExcavating" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FFB81C] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FFB81C] transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FFB81C] transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white"></i>
                </a>
              </div>
            </div>

            {/* Middle Column - Quick Links */}
            <div>
              <h3 className="text-white font-bold text-lg uppercase tracking-wider mb-7">Quick Links</h3>
              <ul className="space-y-4">
                <li>
                  <a href="#services" className="text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                    Services
                  </a>
                </li>
                <li>
                  <a href="#gallery" className="text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                    Gallery
                  </a>
                </li>
                <li>
                  <a href="#about" className="text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#service-area" className="text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                    Service Area
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                    Request Estimate
                  </a>
                </li>
              </ul>
            </div>

            {/* Right Column - Contact */}
            <div>
              <h3 className="text-white font-bold text-lg uppercase tracking-wider mb-7">Get In Touch</h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <i className="ri-phone-line text-[#FFB81C]"></i>
                  <a href="tel:5733745867" className="text-white hover:text-[#FFB81C] transition-colors cursor-pointer">
                    (573) 374-5867
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-mail-line text-[#FFB81C]"></i>
                  <a href="mailto:dunnaway.excavating@gmail.com" className="text-white hover:text-[#FFB81C] transition-colors cursor-pointer">
                    dunnaway.excavating@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-map-pin-line text-[#FFB81C]"></i>
                  <span className="text-gray-400">
                    Gravois Mills, MO 65037<br />
                    Lake of the Ozarks Region
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-global-line text-[#FFB81C]"></i>
                  <span className="text-[#FFB81C]">dunnaway-excavating.com</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2025 Dunnaway Excavating. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <span className="text-[#FFB81C] border border-[#FFB81C] px-4 py-2 rounded-full text-sm font-semibold">
                  Family Owned Since 1966
                </span>
                <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-white hover:underline transition-colors cursor-pointer">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
